import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  Edit3,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Truck,
  Trash2,
} from "lucide-react";
import i18next from "i18next";

import { reisorProMarketRequest } from "../utils/reisorProMarketHttp.js";
import ReisorProExpeditorVehicleModal from "./ReisorProExpeditorVehicleModal.jsx";

const COPY = {
  ru: {
    title: "Мои машины",
    add: "Добавить машину",
    active: "Активные машины",
    history: "История",
    emptyActive: "Активных машин пока нет.",
    emptyHistory: "История машин пока пустая.",
    loading: "Загрузка машин…",
    loadError: "Не удалось загрузить мои машины.",
    edit: "Изменить",
    close: "Закрыть",
    closeConfirm: "Закрыть это объявление машины?",
    closeError: "Не удалось закрыть объявление машины.",
    available: "Свободна с",
    expires: "Актуально до",
    capacity: "Грузоподъёмность",
    volume: "Объём",
    negotiable: "Цена договорная",
    vehicle: "Машина",
    tons: "т",
    cubic: "м³",
    refresh: "Обновить",
    statusClosed: "Закрыто",
    statusExpired: "Истекло",
    partial: "Догруз",
  },
  uz: {
    title: "Менинг машиналарим",
    add: "Машина қўшиш",
    active: "Фаол машиналар",
    history: "Тарих",
    emptyActive: "Ҳозирча фаол машина йўқ.",
    emptyHistory: "Машиналар тарихи ҳозирча бўш.",
    loading: "Машиналар юкланмоқда…",
    loadError: "Менинг машиналаримни юклаб бўлмади.",
    edit: "Ўзгартириш",
    close: "Ёпиш",
    closeConfirm: "Машина эълонини ёпасизми?",
    closeError: "Машина эълонини ёпиб бўлмади.",
    available: "Бўш вақти",
    expires: "Амал қилади",
    capacity: "Юк кўтариши",
    volume: "Ҳажми",
    negotiable: "Нарх келишилади",
    vehicle: "Машина",
    tons: "т",
    cubic: "м³",
    refresh: "Янгилаш",
    statusClosed: "Ёпилган",
    statusExpired: "Муддати ўтган",
    partial: "Қўшимча юк",
  },
  tr: {
    title: "Araçlarım",
    add: "Araç ekle",
    active: "Aktif araçlar",
    history: "Geçmiş",
    emptyActive: "Henüz aktif araç yok.",
    emptyHistory: "Araç geçmişi henüz boş.",
    loading: "Araçlar yükleniyor…",
    loadError: "Araçlarım yüklenemedi.",
    edit: "Düzenle",
    close: "Kapat",
    closeConfirm: "Bu araç ilanı kapatılsın mı?",
    closeError: "Araç ilanı kapatılamadı.",
    available: "Müsait",
    expires: "Geçerli",
    capacity: "Kapasite",
    volume: "Hacim",
    negotiable: "Fiyat görüşülür",
    vehicle: "Araç",
    tons: "t",
    cubic: "m³",
    refresh: "Yenile",
    statusClosed: "Kapalı",
    statusExpired: "Süresi doldu",
    partial: "Ek yük",
  },
};

const BODY_LABELS = {
  ru: { TENT: "Тент", REFRIGERATOR: "Рефрижератор", ISOTHERM: "Изотерм", VAN: "Фургон", CONTAINER: "Контейнер", FLATBED: "Платформа", MEGA: "Mega", OTHER: "Другое" },
  uz: { TENT: "Тент", REFRIGERATOR: "Рефрижератор", ISOTHERM: "Изотерм", VAN: "Фургон", CONTAINER: "Контейнер", FLATBED: "Платформа", MEGA: "Mega", OTHER: "Бошқа" },
  tr: { TENT: "Tenteli", REFRIGERATOR: "Frigorifik", ISOTHERM: "İzoterm", VAN: "Kapalı kasa", CONTAINER: "Konteyner", FLATBED: "Platform", MEGA: "Mega", OTHER: "Diğer" },
};

function text(value) {
  return String(value == null ? "" : value).trim();
}

function languageCode() {
  const raw = text(i18next.language).toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

function cleanToken(value) {
  return text(value).replace(/^Bearer\s+/i, "");
}

function formatDate(value, lang) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatAmount(item, lang, t) {
  if (text(item?.priceMode).toUpperCase() !== "FIXED") return t.negotiable;
  const amount = Number(item?.priceAmount);
  if (!Number.isFinite(amount) || amount <= 0) return t.negotiable;
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount)} ${text(item?.currency)}`;
}

function vehicleName(item) {
  return [text(item?.vehicle?.carBrand), text(item?.vehicle?.carModel), text(item?.vehicle?.carYear)]
    .filter(Boolean)
    .join(" ");
}

function effectiveStatus(item) {
  const status = text(item?.status).toUpperCase();
  if (status && status !== "ACTIVE") return status;
  const expires = item?.expiresAt ? new Date(item.expiresAt) : null;
  if (expires && !Number.isNaN(expires.getTime()) && expires.getTime() <= Date.now()) return "EXPIRED";
  return "ACTIVE";
}

export default function ReisorProExpeditorMyVehicles({ token = "", onBack }) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const bodyLabels = BODY_LABELS[lang] || BODY_LABELS.ru;
  const normalizedToken = useMemo(() => cleanToken(token), [token]);

  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [closingId, setClosingId] = useState("");

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!normalizedToken) {
      setItems([]);
      setError(t.loadError);
      return;
    }

    if (!silent) setBusy(true);
    setError("");

    try {
      const { response, data } = await reisorProMarketRequest("/expeditor/vehicles", {
        token: normalizedToken,
      });

      if (!response?.ok || !data?.ok) throw new Error(text(data?.error) || "MY_VEHICLES_FAILED");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setItems([]);
      setError(t.loadError);
    } finally {
      if (!silent) setBusy(false);
    }
  }, [normalizedToken, t.loadError]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleItems = useMemo(
    () => items.filter((item) => (tab === "active" ? effectiveStatus(item) === "ACTIVE" : effectiveStatus(item) !== "ACTIVE")),
    [items, tab]
  );

  async function closeVehicle(item) {
    const id = text(item?.id);
    if (!id || closingId || !window.confirm(t.closeConfirm)) return;

    setClosingId(id);
    setError("");
    try {
      const { response } = await reisorProMarketRequest(`/expeditor/vehicles/${encodeURIComponent(id)}/close`, {
        method: "POST",
        token: normalizedToken,
      });
      if (!response?.ok) throw new Error("CLOSE_FAILED");
      await load({ silent: true });
    } catch {
      setError(t.closeError);
    } finally {
      setClosingId("");
    }
  }

  return (
    <section className="space-y-4" data-reisor-expeditor-my-vehicles="v1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            aria-label={t.title}
          >
            <ArrowLeft size={21} />
          </button>
          <h1 className="truncate text-2xl font-black text-slate-950 dark:text-white">{t.title}</h1>
        </div>

        <button
          type="button"
          onClick={() => void load()}
          disabled={busy}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          aria-label={t.refresh}
          title={t.refresh}
        >
          <RefreshCw size={19} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => {
          setEditingVehicle(null);
          setModalOpen(true);
        }}
        className="flex min-h-[58px] w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-5 text-base font-black text-white shadow-sm"
      >
        <Truck size={22} />
        <span>{t.add}</span>
        <Plus size={22} />
      </button>

      <div className="grid grid-cols-2 rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
        <button
          type="button"
          onClick={() => setTab("active")}
          className={`min-h-[52px] rounded-xl px-3 text-sm font-black ${tab === "active" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300"}`}
        >
          {t.active}
        </button>
        <button
          type="button"
          onClick={() => setTab("history")}
          className={`min-h-[52px] rounded-xl px-3 text-sm font-black ${tab === "history" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300"}`}
        >
          <span className="inline-flex items-center gap-2"><History size={17} />{t.history}</span>
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-300">{error}</div>
      ) : null}

      {busy && items.length === 0 ? (
        <div className="flex min-h-[180px] items-center justify-center rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
            <Loader2 size={18} className="animate-spin" /> {t.loading}
          </div>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 text-center dark:border-slate-800 dark:bg-slate-900">
          <Truck size={58} className="text-slate-300 dark:text-slate-600" />
          <div className="text-base font-bold text-slate-400 dark:text-slate-500">
            {tab === "active" ? t.emptyActive : t.emptyHistory}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleItems.map((item) => {
            const status = effectiveStatus(item);
            const car = vehicleName(item);
            const body = bodyLabels[text(item?.bodyType).toUpperCase()] || text(item?.bodyType) || "—";

            return (
              <article key={text(item?.id)} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-base font-black text-slate-950 dark:text-white">
                      <span className="truncate">{text(item?.from) || "—"}</span>
                      <span className="text-slate-400">→</span>
                      <span className="truncate">{text(item?.to) || "—"}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-[13px] font-bold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">{body}</span>
                      {item?.partialLoad ? <span className="rounded-full bg-amber-50 px-2 py-1 text-[13px] font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">{t.partial}</span> : null}
                      {status !== "ACTIVE" ? <span className="rounded-full bg-slate-100 px-2 py-1 text-[13px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{status === "EXPIRED" ? t.statusExpired : t.statusClosed}</span> : null}
                    </div>
                  </div>
                  <Truck size={24} className="shrink-0 text-slate-400" />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/60">
                    <div className="text-[14px] text-slate-500 dark:text-slate-400">{t.capacity}</div>
                    <div className="mt-1 font-black text-slate-900 dark:text-slate-100">{Number(item?.capacityTons) > 0 ? `${Number(item.capacityTons)} ${t.tons}` : "—"}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/60">
                    <div className="text-[14px] text-slate-500 dark:text-slate-400">{t.volume}</div>
                    <div className="mt-1 font-black text-slate-900 dark:text-slate-100">{item?.volumeM3 == null ? "—" : `${Number(item.volumeM3)} ${t.cubic}`}</div>
                  </div>
                </div>

                {car ? <div className="mt-3 text-sm text-slate-600 dark:text-slate-300"><span className="text-slate-400">{t.vehicle}: </span><strong>{car}</strong></div> : null}

                <div className="mt-3 space-y-1.5 text-[14px] text-slate-500 dark:text-slate-400">
                  <div className="flex items-center gap-2"><CalendarDays size={15} /><span>{t.available}: {formatDate(item?.availableFrom, lang)}</span></div>
                  <div className="flex items-center gap-2"><CalendarDays size={15} /><span>{t.expires}: {formatDate(item?.expiresAt, lang)}</span></div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 dark:border-slate-800">
                  <div className="text-sm font-black text-emerald-700 dark:text-emerald-300">{formatAmount(item, lang, t)}</div>
                  {status === "ACTIVE" ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingVehicle(item);
                          setModalOpen(true);
                        }}
                        className="flex min-h-[40px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-[14px] font-black text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      >
                        <Edit3 size={14} /> {t.edit}
                      </button>
                      <button
                        type="button"
                        onClick={() => void closeVehicle(item)}
                        disabled={closingId === text(item?.id)}
                        className="flex min-h-[40px] items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 text-[14px] font-black text-red-700 disabled:opacity-50 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-300"
                      >
                        {closingId === text(item?.id) ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {t.close}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <ReisorProExpeditorVehicleModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingVehicle(null);
        }}
        token={normalizedToken}
        vehicle={editingVehicle}
        onSaved={() => {
          setModalOpen(false);
          setEditingVehicle(null);
          void load({ silent: true });
        }}
      />
    </section>
  );
}
